import { type Property, type PropertyCard as PropertyCardData } from '@resolute/schema';
import { Badge, Button, cn, Container, Section } from '@resolute/ui';
import { formatINRCompact } from '@resolute/utils';
import { BadgeCheck, Building2, GitCompare, Heart, MapPin, ShieldCheck } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { Link, useParams } from 'react-router-dom';

import { useProperty, useRelatedProperties } from './api';
import { ContactCard } from './components/contact-card';
import { DetailSkeleton } from './components/detail-skeleton';
import { PropertyDocuments } from './components/documents';
import { Gallery } from './components/gallery';
import { InvestmentPanel } from './components/investment-panel';
import { LocationMap } from './components/location-map';
import { UnitTypesTable } from './components/unit-types-table';
import { useDetailFacts } from './use-detail-facts';

/** Lazy so recharts stays out of this route's chunk — see the render site. */
const PriceTrend = lazy(() =>
  import('./components/price-trend').then((m) => ({ default: m.PriceTrend })),
);

import { Reveal, StaggerGroup, StaggerItem } from '@/components/motion/reveal';
import { PropertyCard } from '@/components/property/property-card';
import { useCompare } from '@/features/engagement/compare-context';
import { useWishlist } from '@/features/engagement/wishlist-context';
import { sanitizeHtml } from '@/lib/sanitize-html';
import { Seo } from '@/lib/seo';

export default function PropertyDetailPage() {
  const { slug = '' } = useParams();
  const { data: property, isLoading, isError } = useProperty(slug);
  const { data: related } = useRelatedProperties(slug);

  if (isLoading) return <DetailSkeleton />;

  if (isError || !property) {
    return (
      <>
        <Seo title="Property not found" noindex />
        <Container className="flex min-h-[50vh] flex-col items-center justify-center gap-4 py-20 text-center">
          <h1 className="text-2xl font-semibold">Property not found</h1>
          <p className="text-muted-foreground">
            This listing may have been sold or removed.
          </p>
          <Link to="/buy" className="text-primary hover:underline">
            Browse other properties →
          </Link>
        </Container>
      </>
    );
  }

  return <DetailView property={property} related={related} slug={slug} />;
}

function DetailView({
  property,
  related,
  slug,
}: {
  property: Property;
  related?: PropertyCardData[];
  slug: string;
}) {
  const wishlist = useWishlist();
  const compare = useCompare();
  // Every derived value — which facts are real, which would repeat the table,
  // which columns carry information — lives in `useDetailFacts`.
  const {
    labels,
    card,
    cityLabel,
    unitTypes,
    hasMultipleUnitTypes,
    facts,
    inventoryFacts,
    addressFacts,
    columns,
    jsonLd,
  } = useDetailFacts(property);
  const saved = wishlist.has(property.id);
  const comparing = compare.has(property.id);

  return (
    <>
      <Seo
        title={property.title}
        description={
          property.seo?.description ??
          `${property.title} in ${property.location.locality}, ${cityLabel}. ${formatINRCompact(property.price)}. Verified listing with transparent pricing on Resolute Property Solutions.`
        }
        path={`/property/${property.slug}`}
        image={card.coverImage?.url}
        jsonLd={jsonLd}
      />

      {/* Sticky property identity bar — stays visible while scrolling so the
          user always knows which listing they're on. Sits full-bleed, right
          below the main site header (same offset as its height). */}
      {/* Sits flush under the site header via `--header-offset` rather than a
          hardcoded height, so when the header retracts on scroll-down this bar
          rides up to the top edge instead of stranding a 64px band of scrolling
          content above it. See global.css. */}
      <div className="sticky top-[var(--header-offset)] z-30 border-b border-border/70 bg-background/90 backdrop-blur-md backdrop-saturate-150">
        <Container className="flex flex-wrap items-center justify-between gap-4 py-3 lg:py-4">
          <div className="min-w-0">
            <h1 className="truncate font-display text-xl font-semibold tracking-tight sm:text-2xl">
              {property.title}
            </h1>
            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" aria-hidden="true" />
              {property.location.locality}, {cityLabel}
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button
              variant={saved ? 'primary' : 'outline'}
              size="sm"
              onClick={() => wishlist.toggle(card)}
              aria-pressed={saved}
            >
              <Heart className={cn('size-4', saved && 'fill-current')} aria-hidden="true" />
              {saved ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant={comparing ? 'primary' : 'outline'}
              size="sm"
              onClick={() => compare.toggle(card)}
              aria-pressed={comparing}
            >
              <GitCompare className="size-4" aria-hidden="true" /> Compare
            </Button>
          </div>
        </Container>
      </div>

      <Section spacing="sm">
        <Container>
          <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            {/* Main column — capped narrower than the available grid track so the
                gallery, price/status card, key facts, overview, amenities etc. all
                share one consistent right edge instead of stretching full width. */}
            <div className="flex min-w-0 max-w-4xl flex-col gap-10">
              <Gallery media={property.media ?? []} title={property.title} />

              {/* Every section below reveals independently on its own scroll
                  entry, matching the rest of the site — this page previously
                  had no scroll-reveal treatment at all. */}
              {/* The "Pricing and status" card that used to sit here (guide
                  price, per-sq-ft rate, "Marketed by", status/GIFT City badges)
                  is gone: with prices unpublished it led with a value it could
                  no longer state. Nothing was lost with it — the status is a
                  row in Key facts below, and the developer is named in the
                  verification card immediately following. */}

              {/* Trust / RERA */}
              {(property.rera?.number || property.developer) && (
                <Reveal>
                  <section
                    aria-labelledby="trust-heading"
                    className="rounded-lg border border-border bg-card p-5"
                  >
                    <h2 id="trust-heading" className="text-lg font-semibold">
                      Verification &amp; developer
                    </h2>
                    {/* A RERA number is one 60-character slash-run with no
                        spaces ("PR/GJ/GANDHINAGAR/…/290925"). A flex item
                        defaults to `min-width: auto`, so it refused to shrink
                        below that token and forced the whole document to 620px
                        wide — on a phone the browser then zooms out to fit,
                        which is what made every page look shrunken and
                        left-shifted. `min-w-0` lets the item shrink and
                        `break-all` gives the token somewhere to wrap; `items-start`
                        keeps the icon aligned to the first line once it does. */}
                    <div className="mt-3 flex flex-col gap-3 text-sm">
                      {property.rera?.number && (
                        <p className="flex flex-wrap items-start gap-x-2 gap-y-1">
                          <ShieldCheck
                            className="mt-0.5 size-4 shrink-0 text-success"
                            aria-hidden="true"
                          />
                          <span className="shrink-0">RERA:</span>
                          <span className="min-w-0 break-all font-medium">
                            {property.rera.number}
                          </span>
                          {property.rera.verified && <Badge variant="success">Verified</Badge>}
                        </p>
                      )}
                      {property.developer && (
                        <p className="flex items-start gap-2">
                          <Building2
                            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                            aria-hidden="true"
                          />
                          <span className="shrink-0">Developer:</span>
                          <span className="min-w-0 break-words font-medium">
                            {property.developer.name}
                          </span>
                        </p>
                      )}
                    </div>
                  </section>
                </Reveal>
              )}

              {/* Key facts */}
              <Reveal>
                <section aria-labelledby="facts-heading">
                  <h2 id="facts-heading" className="sr-only">
                    Key facts
                  </h2>
                  <dl className="grid grid-cols-2 gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-3">
                    {facts.map((f) => (
                      <div key={f.label}>
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {f.label}
                        </dt>
                        <dd className="mt-1 font-medium capitalize">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </Reveal>

              {inventoryFacts.length > 0 && (
                <Reveal>
                  <section aria-labelledby="inventory-heading">
                    <h2 id="inventory-heading" className="text-lg font-semibold">
                      Inventory details
                    </h2>
                    <dl className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-4">
                      {inventoryFacts.map((f) => (
                        <div key={f.label}>
                          <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                            {f.label}
                          </dt>
                          <dd className="mt-1 font-medium">{f.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </section>
                </Reveal>
              )}

              {hasMultipleUnitTypes && (
                <Reveal>
                  <UnitTypesTable
                    unitTypes={unitTypes}
                    columns={columns}
                    typeLabel={labels.type}
                  />
                </Reveal>
              )}

              {property.isPreLeased && property.investment && (
                <Reveal>
                  <InvestmentPanel investment={property.investment} price={property.price} />
                </Reveal>
              )}

              {property.description && (
                <Reveal>
                  <section aria-labelledby="overview-heading">
                    <h2 id="overview-heading" className="text-lg font-semibold">
                      Overview
                    </h2>
                    <div
                      // `break-words` is a backstop only: `sanitizeHtml` already
                      // collapses CPzone's word-separating `&nbsp;` so prose wraps
                      // normally. This catches the genuinely unbreakable tokens it
                      // can't help with (a long URL, a slash-run RERA number), so
                      // they wrap instead of overflowing under the contact rail.
                      className="mt-3 space-y-3 break-words leading-relaxed text-muted-foreground [&_a]:underline [&_a]:underline-offset-2 [&_li]:ml-5 [&_ol]:list-decimal [&_ul]:list-disc"
                      // CPzone's description field is rich-text HTML (e.g. `<p><strong>...`),
                      // not plain text — rendering it as text would show the raw tags.
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(property.description) }}
                    />
                  </section>
                </Reveal>
              )}

              {property.amenities.length > 0 && (
                <Reveal>
                  <section aria-labelledby="amenities-heading">
                    <h2 id="amenities-heading" className="text-lg font-semibold">
                      Amenities
                    </h2>
                    <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {property.amenities.map((a) => (
                        <li key={a} className="flex items-center gap-2 text-sm">
                          <BadgeCheck className="size-4 text-primary" aria-hidden="true" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </section>
                </Reveal>
              )}

              {/* Brochures / floor plans. The API has always returned these on
                  `documents` — nothing rendered them until now. */}
              {(property.documents?.length ?? 0) > 0 && (
                <Reveal>
                  <PropertyDocuments documents={property.documents} title={property.title} />
                </Reveal>
              )}

              {/* `PriceTrend` statically imports recharts (~112 kB gzip), which
                  made it a hard dependency of this route's chunk. It renders
                  nothing without a rate, and the API sends no `pricePerSqft`
                  today, so the whole chart library was being downloaded to
                  render null on every property page.
                  The guard lives INSIDE `Reveal`, not around it: `Reveal`
                  always renders a div, and this column is `flex flex-col
                  gap-10`, so an empty wrapper still occupies a 40px gap slot.
                  Dropping the wrapper would pull everything below it up by
                  40px — keeping it preserves the layout exactly while the
                  lazy chunk stays unfetched. */}
              <Reveal>
                {property.pricePerSqft != null && (
                  <Suspense fallback={null}>
                    <PriceTrend pricePerSqft={property.pricePerSqft} />
                  </Suspense>
                )}
              </Reveal>

              {/* Location */}
              <Reveal>
                <section aria-labelledby="location-heading">
                  <h2 id="location-heading" className="text-lg font-semibold">
                    Location
                  </h2>
                  {/* Full address breakdown rather than one summary line, so
                      every part CPzone actually holds is visible. Parts it has
                      no value for are simply absent (see `addressFacts`). */}
                  <dl className="mt-3 grid grid-cols-2 gap-4 rounded-lg border border-border bg-card p-5 sm:grid-cols-3">
                    {addressFacts.map((f) => (
                      <div
                        key={f.label}
                        className={
                          f.label === 'Address' || f.label.startsWith('Nearby')
                            ? 'col-span-2 sm:col-span-3'
                            : undefined
                        }
                      >
                        <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                          {f.label}
                        </dt>
                        <dd className="mt-1 font-medium">{f.value}</dd>
                      </div>
                    ))}
                  </dl>
                  {property.location.googleMapsUrl && (
                    <a
                      href={property.location.googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                    >
                      <MapPin className="size-4" aria-hidden="true" />
                      Open in Google Maps
                    </a>
                  )}
                  {property.location.point?.coordinates ? (
                    <LocationMap
                      lng={property.location.point.coordinates[0]}
                      lat={property.location.point.coordinates[1]}
                      title={property.title}
                    />
                  ) : (
                    <div className="mt-4 flex aspect-[16/7] items-center justify-center rounded-lg border border-dashed border-border bg-muted/40 text-sm text-muted-foreground">
                      Map location not available for this listing
                    </div>
                  )}
                </section>
              </Reveal>

            </div>

            {/* Sticky contact rail — `top` matches the aside's actual static
                position at scroll=0 (header 64px + identity bar ~86px + this
                section's own `sm:py-10` top padding 40px at the lg breakpoint =
                190px), not just "enough to clear both bars". A smaller offset
                (e.g. `top-40`/160px) still clears both bars but is LESS than
                the natural starting position, so the rail visibly drifts
                upward for the first ~30px of scroll before locking in —
                matching the offset to the real starting distance removes that
                catch-up drift entirely.
                The 126px is that total MINUS the header, expressed via
                `--header-offset` so the rail tracks the header retracting the
                same way the identity bar above it does (126 + 64 = the original
                190 while the header is shown, 126 once it has slid away).
                Re-check the 126 if the identity bar content or this section's
                spacing tier ever change. */}
            <aside className="lg:sticky lg:top-[calc(126px+var(--header-offset))] lg:self-start">
              <ContactCard slug={slug} title={property.title} />
            </aside>
          </div>

          {/* Related */}
          {related && related.length > 0 && (
            <section aria-labelledby="related-heading" className="mt-16">
              <Reveal>
                <h2
                  id="related-heading"
                  className="mb-6 font-display text-2xl font-semibold tracking-tight"
                >
                  Similar properties
                </h2>
              </Reveal>
              <StaggerGroup className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((p) => (
                  <StaggerItem key={p.id}>
                    <PropertyCard property={p} />
                  </StaggerItem>
                ))}
              </StaggerGroup>
            </section>
          )}
        </Container>
      </Section>
    </>
  );
}
